import { FormlyFieldConfig } from '@ngx-formly/core';
import {
  IApplicationFormData,
  IFieldArrayData,
  IFieldArrayDataFields,
  IFieldData,
  IFieldGroupNameData,
} from '../../ui/models/application-form-data.model';
import { TDoubleIdentityFormControlValue } from '../../ui/models/citizen-document-types.enum';
import { IServiceTranslation } from '../../ui/models/service-translation.model';
import { TranslateService } from '@ngx-translate/core';
import { cleanServiceData } from './application-form-shared.util';
import {
  buildCustomDatatablePreviewData,
  buildCustomMultiSelectDataFetchPreviewData,
  buildCustomTableWithInputPreviewData,
} from './application-form.util';

interface IGetDatFieldsData {
  fieldGroup: IFieldData[];
  fieldArray?: IFieldArrayData[];
}

export const applicationFormBuildPreviewDataTranslated = (
  layout: FormlyFieldConfig[],
  layoutData: Record<string, unknown>,
  serviceTranslations?: IServiceTranslation,
  addLabelCount = false,
  translateService?: TranslateService
): IApplicationFormData[] => {
  if (!layout[0].fieldGroup) {
    return [];
  }

  // Handle customsingleservicerender type
  if (layout[0].type === 'customsingleservicerender') {
    return processSingleServiceRender(
      layout[0],
      layoutData,
      serviceTranslations,
      addLabelCount,
      translateService,
      applicationFormBuildPreviewDataTranslated
    );
  }

  return layout[0].fieldGroup
    .filter(section => !section.hide || section.props?.['hideFromPreview'])
    .map(section => {
      const sectionName = section?.props?.label as string;
      const sectionKey = section?.key as string;
      const formGroup: IFieldGroupNameData[] = [];
      let servicePreviewData: IApplicationFormData[] = [];

      // Handle customsingleservicerender sections
      if (section.type === 'customsingleservicerender') {
        servicePreviewData = processSingleServiceRender(
          section,
          layoutData,
          serviceTranslations,
          addLabelCount,
          translateService,
          applicationFormBuildPreviewDataTranslated
        );

        // Service sections are now handled directly in the return statement
        // No need to add them to the regular formGroup as they use nestedSections
      } else {
        // Regular section processing with fieldGroup
        section.fieldGroup?.forEach(block => {
          if (block.props?.['hideFromPreview']) {
            return;
          }
          const fieldGroupName = block?.props?.label as string;
          const fieldGroupKey = block?.key as string;
          const fieldGroupData: IGetDatFieldsData = getFieldData(
            section,
            block,
            layoutData,
            serviceTranslations,
            addLabelCount,
            false,
            translateService
          );

          if (
            fieldGroupData.fieldGroup.length > 0 ||
            fieldGroupData?.fieldArray?.length
          ) {
            formGroup.push({
              fieldGroupName,
              fieldGroupKey,
              fieldGroup:
                fieldGroupData.fieldGroup.length > 0
                  ? fieldGroupData.fieldGroup
                  : undefined,
              fieldArray:
                fieldGroupData?.fieldArray?.length &&
                fieldGroupData.fieldArray.length > 0
                  ? fieldGroupData.fieldArray
                  : undefined,
            });
          }
        });
      }

      // Check if this section is a service and return the service data
      if (
        section.type === 'customsingleservicerender' &&
        servicePreviewData.length > 0
      ) {
        return servicePreviewData[0]; // Return the service section with nestedSections
      }

      return {
        formGroupName: sectionName,
        formGroupKey: sectionKey,
        formGroup,
      };
    })
    .filter(
      x =>
        x.formGroup.length > 0 ||
        (x.isService && x.nestedSections && x.nestedSections.length > 0)
    );
};

const getFieldData = (
  section: FormlyFieldConfig,
  block: FormlyFieldConfig,
  layoutData: Record<string, unknown>,
  serviceTranslations?: IServiceTranslation,
  addLabelCount = false,
  isNestedBlock = false,
  translateService?: TranslateService
): IGetDatFieldsData => {
  const fieldGroup: IFieldData[] = [];
  let fieldArray: IFieldArrayData[] | undefined = undefined;
  block.fieldGroup?.forEach((field, order) => {
    if (field.props?.['hideFromPreview']) {
      return;
    }

    let value = (
      (layoutData?.[section?.key as string] as Record<string, unknown>)?.[
        block?.key as string
      ] as Record<string, unknown>
    )?.[field?.key as string];

    if (isNestedBlock) {
      value = (layoutData?.[block?.key as string] as Record<string, unknown>)?.[
        field?.key as string
      ];
    }

    if (!value) {
      return;
    }

    switch (field.type) {
      case 'block': {
        // Handle nested blocks (e.g., block inside block inside repeater)
        // We need to call getNestedFields and wrap the result in a fieldArray structure
        // so the template can render it recursively through the nestedBlock template
        const nestedBlockResult = getNestedFields(
          field,
          addLabelCount,
          order,
          section,
          layoutData[block.key as string] as Record<string, unknown>,
          serviceTranslations,
          order,
          translateService
        );

        if (nestedBlockResult) {
          // Wrap the nested block in a fieldArray structure for template rendering
          const wrappedNestedBlock: IFieldArrayData = {
            fieldArrayKey: `${field.key as string}_nested_block_wrapper`,
            arrayItems: [
              {
                fieldGroup: [],
                formGroup: [nestedBlockResult],
              },
            ],
            order,
          };

          if (!fieldArray) {
            fieldArray = [wrappedNestedBlock];
          } else {
            fieldArray.push(wrappedNestedBlock);
          }
        }
        break;
      }
      case 'customcascadingdropdowns':
        field?.props?.['configs'].forEach((ele: Record<string, unknown>) => {
          if ((value as Record<string, unknown>)[ele['key'] as string]) {
            fieldGroup.push({
              fieldType: 'customdropdown',
              fieldBindLabel: ele['bindlabel'],
              fieldName: ele['key'] as string,
              fieldLabel: ele['label'] as string,
              fieldParentKey: field['key'] as string,
              fieldValue: (value as Record<string, unknown>)[
                ele['key'] as string
              ],
              order,
            });
          }
        });
        break;
      case 'customslotbooking':
        field?.props?.['configs'].forEach((ele: Record<string, unknown>) => {
          if ((value as Record<string, unknown>)[ele['key'] as string]) {
            if (ele['isSeats']) {
              fieldGroup.push({
                fieldType: 'number',
                fieldBindLabel: '',
                fieldName: ele['key'] as string,
                fieldLabel: ele['label'] as string,
                fieldParentKey: field['parent'] as string,
                fieldValue: (value as Record<string, unknown>)[
                  ele['key'] as string
                ],
                order,
              });
            } else {
              fieldGroup.push({
                fieldType: 'customdropdown',
                fieldBindLabel: ele['bindlabel'],
                fieldName: ele['key'] as string,
                fieldLabel: ele['label'] as string,
                fieldParentKey: field['parent'] as string,
                fieldValue: (value as Record<string, unknown>)[
                  ele['key'] as string
                ],
                order,
              });
            }
          }
        });
        break;
      case 'customdoubleidinput':
        Object.keys(<TDoubleIdentityFormControlValue>value).forEach(key => {
          if ((<TDoubleIdentityFormControlValue>value)[key]) {
            fieldGroup.push({
              fieldType: 'customdoubleidinput',
              fieldBindLabel: '',
              fieldName: key,
              fieldLabel:
                (<TDoubleIdentityFormControlValue>value)[key]?.idLabel ?? '',
              fieldParentKey: field['key'] as string,
              fieldValue: (<TDoubleIdentityFormControlValue>value)[key],
              order,
            });
          }
        });
        break;
      case 'customrepeater': {
        const customRepeaterField: IFieldArrayData = {
          fieldArrayKey: field.key as string,
          arrayItems: [],
          order,
        };

        const fieldArrayGroup: FormlyFieldConfig[] =
          (<FormlyFieldConfig>field.fieldArray)?.fieldGroup ?? [];

        if (fieldArrayGroup.length > 0 && typeof value === 'object') {
          customRepeaterField.arrayItems = Object.keys(value).map(
            (_key, index: number) => {
              const fieldGroup: IFieldData[] = [];
              const formGroup: IFieldGroupNameData[] = [];
              const repeaterFieldValue = (<Record<string, unknown>>value)[
                index
              ];
              fieldArrayGroup.forEach((repeaterField: FormlyFieldConfig) => {
                switch (repeaterField.type) {
                  case 'block': {
                    const formGroupData = getNestedFields(
                      repeaterField,
                      addLabelCount,
                      index,
                      section,
                      repeaterFieldValue as Record<string, unknown>,
                      serviceTranslations,
                      index,
                      translateService
                    );
                    if (formGroupData) {
                      formGroup.push(formGroupData);
                    }
                    break;
                  }
                  case 'customcascadingdropdowns': {
                    fieldGroup.push(
                      ...createLocationFields(
                        repeaterField?.props?.['configs'],
                        repeaterFieldValue,
                        repeaterField,
                        repeaterField['key'] as string,
                        index
                      )
                    );
                    break;
                  }
                  case 'customdatatable': {
                    buildCustomDatatablePreviewData(
                      (<Record<string, unknown>>repeaterFieldValue)[
                        repeaterField.key as string
                      ],
                      fieldGroup,
                      repeaterField,
                      index
                    );
                    break;
                  }
                  case 'customtablewithinputform': {
                    buildCustomTableWithInputPreviewData(
                      (<Record<string, unknown>>repeaterFieldValue)[
                        repeaterField.key as string
                      ],
                      fieldGroup,
                      repeaterField,
                      index
                    );
                    break;
                  }
                  case 'customrepeater': {
                    // Handle nested customrepeater
                    const nestedRepeaterData = processNestedRepeater(
                      repeaterField,
                      repeaterFieldValue as Record<string, unknown>,
                      section,
                      serviceTranslations,
                      addLabelCount,
                      index,
                      translateService,
                      false // isCustomDynamicRepeater
                    );
                    if (nestedRepeaterData) {
                      formGroup.push(nestedRepeaterData);
                    }
                    break;
                  }
                  case 'customdynamicrepeater': {
                    // Handle nested customdynamicrepeater
                    const nestedRepeaterData = processNestedRepeater(
                      repeaterField,
                      repeaterFieldValue as Record<string, unknown>,
                      section,
                      serviceTranslations,
                      addLabelCount,
                      index,
                      translateService,
                      true // isCustomDynamicRepeater
                    );
                    if (nestedRepeaterData) {
                      formGroup.push(nestedRepeaterData);
                    }
                    break;
                  }
                  default: {
                    if (
                      (<Record<string, unknown>>repeaterFieldValue)[
                        repeaterField.key as string
                      ]
                    ) {
                      fieldGroup.push({
                        fieldType: repeaterField.type as string,
                        fieldBindLabel:
                          (repeaterField?.props?.['bindLabel'] as string) ?? '',
                        fieldName: repeaterField.key as string,
                        fieldLabel: (repeaterField?.props?.label +
                          setCountOnLabel(addLabelCount, index)) as string,
                        fieldValue: (<Record<string, unknown>>(
                          repeaterFieldValue
                        ))[repeaterField.key as string],
                        order: index,
                      });
                    }
                    break;
                  }
                }
              });
              return {
                fieldGroup,
                formGroup,
              };
            }
          );
        }

        if (!fieldArray) {
          fieldArray = [customRepeaterField];
          break;
        }

        fieldArray.push(customRepeaterField);
        break;
      }
      case 'customdynamicrepeater': {
        const customDynamicRepeaterField: IFieldArrayData = {
          fieldArrayKey: field.key as string,
          arrayItems: [],
          order,
        };

        const fieldArrayGroup: FormlyFieldConfig[] =
          (<FormlyFieldConfig>field.fieldArray)?.fieldGroup ?? [];

        if (
          fieldArrayGroup.length > 0 &&
          typeof value === 'object' &&
          Array.isArray(value)
        ) {
          // Get the dynamic repeater configuration
          const dynamicConfig = field.props?.['dynamicRepeaterConfig'] || {};
          const itemIdField = dynamicConfig.itemIdField || 'id';
          const itemLabelField = dynamicConfig.itemLabelField || 'label';

          // Get items data from props if available (for API-populated items)
          const itemsData = field.props?.['items'] || [];

          customDynamicRepeaterField.arrayItems = value.map(
            (repeaterFieldValue: any, index: number) => {
              const fieldGroup: IFieldData[] = [];
              const formGroup: IFieldGroupNameData[] = [];

              // Get the corresponding item data for dynamic labels
              const itemData = itemsData[index] || {};
              const itemLabel =
                getNestedValue(itemData, itemLabelField) || `Item ${index + 1}`;
              const itemId = getNestedValue(itemData, itemIdField);

              // Create a context for label interpolation
              const labelContext = {
                itemLabel,
                itemId,
                label: itemLabel,
                id: itemId,
                ...itemData, // Include all item data for flexible templating
              };

              fieldArrayGroup.forEach((repeaterField: FormlyFieldConfig) => {
                // Skip hidden fields like itemId and _isApiPopulated
                if (
                  repeaterField.props?.['hideField'] ||
                  repeaterField.key === 'itemId' ||
                  repeaterField.key === '_isApiPopulated'
                ) {
                  return;
                }

                switch (repeaterField.type) {
                  case 'block': {
                    // For nested blocks, we need to pass the label context
                    const formGroupData = getNestedFieldsWithContext(
                      repeaterField,
                      addLabelCount,
                      index,
                      section,
                      repeaterFieldValue as Record<string, unknown>,
                      serviceTranslations,
                      index,
                      translateService,
                      labelContext
                    );
                    if (formGroupData) {
                      // Apply label interpolation to the block name
                      formGroupData.fieldGroupName = interpolateLabel(
                        formGroupData.fieldGroupName,
                        labelContext
                      );
                      formGroup.push(formGroupData);
                    }
                    break;
                  }
                  case 'customcascadingdropdowns': {
                    fieldGroup.push(
                      ...createLocationFields(
                        repeaterField?.props?.['configs'],
                        repeaterFieldValue,
                        repeaterField,
                        repeaterField['key'] as string,
                        index
                      )
                    );
                    break;
                  }
                  case 'customrepeater': {
                    // Handle nested customrepeater
                    const nestedRepeaterData = processNestedRepeater(
                      repeaterField,
                      repeaterFieldValue as Record<string, unknown>,
                      section,
                      serviceTranslations,
                      addLabelCount,
                      index,
                      translateService,
                      false // isCustomDynamicRepeater
                    );
                    if (nestedRepeaterData) {
                      // Apply label interpolation to the nested repeater name
                      nestedRepeaterData.fieldGroupName = interpolateLabel(
                        nestedRepeaterData.fieldGroupName,
                        labelContext
                      );
                      formGroup.push(nestedRepeaterData);
                    }
                    break;
                  }
                  case 'customdynamicrepeater': {
                    // Handle nested customdynamicrepeater
                    const nestedRepeaterData = processNestedRepeater(
                      repeaterField,
                      repeaterFieldValue as Record<string, unknown>,
                      section,
                      serviceTranslations,
                      addLabelCount,
                      index,
                      translateService,
                      true // isCustomDynamicRepeater
                    );
                    if (nestedRepeaterData) {
                      // Apply label interpolation to the nested repeater name
                      nestedRepeaterData.fieldGroupName = interpolateLabel(
                        nestedRepeaterData.fieldGroupName,
                        labelContext
                      );
                      formGroup.push(nestedRepeaterData);
                    }
                    break;
                  }
                  default: {
                    if (
                      (<Record<string, unknown>>repeaterFieldValue)[
                        repeaterField.key as string
                      ]
                    ) {
                      // Interpolate the label with the dynamic context
                      const interpolatedLabel = interpolateLabel(
                        repeaterField?.props?.label as string,
                        labelContext
                      );

                      fieldGroup.push({
                        fieldType: repeaterField.type as string,
                        fieldBindLabel:
                          (repeaterField?.props?.['bindLabel'] as string) ?? '',
                        fieldName: repeaterField.key as string,
                        fieldLabel: interpolatedLabel,
                        fieldValue: (<Record<string, unknown>>(
                          repeaterFieldValue
                        ))[repeaterField.key as string],
                        order: index,
                      });
                    }
                    break;
                  }
                }
              });
              return {
                fieldGroup,
                formGroup,
              };
            }
          );
        }

        if (!fieldArray) {
          fieldArray = [customDynamicRepeaterField];
          break;
        }

        fieldArray.push(customDynamicRepeaterField);
        break;
      }
      case 'customdatatable': {
        buildCustomDatatablePreviewData(value, fieldGroup, field, order);
        break;
      }
      case 'customeditabletable': {
        if (!value || typeof value !== 'object') {
          break;
        }

        const editableTableData = value as Record<string, Record<string, any>>;
        const rows = field.props?.['rows'] || [];
        const columns = field.props?.['columns'] || [];
        const showRowLabels = field.props?.['showRowLabels'] ?? true;
        const rowColumnTitle = field.props?.['rowColumnTitle'] || '';

        fieldGroup.push({
          fieldType: 'customeditabletable',
          fieldBindLabel: '',
          fieldName: field.key as string,
          fieldLabel: field.props?.label || '',
          fieldValue: editableTableData,
          order,
          additionalData: {
            rows,
            columns,
            showRowLabels,
            rowColumnTitle,
          },
        });
        break;
      }
      case 'customitemselectiontable': {
        // Same as customdatatable - just display the value as a table
        buildCustomDatatablePreviewData(value, fieldGroup, field, order);
        break;
      }
      case 'customtablewithinputform': {
        buildCustomTableWithInputPreviewData(value, fieldGroup, field, order);
        break;
      }
      case 'custommultiselectdatafetch': {
        buildCustomMultiSelectDataFetchPreviewData(
          value,
          fieldGroup,
          field,
          order,
          layoutData,
          section,
          block,
          isNestedBlock
        );
        break;
      }
      case 'radio':
      case 'checkbox':
      case 'multicheckbox':
      case 'select':
      case 'customdropdown':
      case 'customdropdownpaginated':
      case 'customdropdowndatafetchpaginated': {
        if (!value || (value instanceof String && value?.length <= 0)) {
          break;
        }
        // Label lookup when showLabelInSummary is enabled
        // Initially the dropdown was using the value in the summary,
        // this change is backward compatible and it will use the label for configures itesm
        if (field.props?.['showLabelInSummary'] === true) {
          const bindLabel = field.props?.['bindLabel'] || 'label';
          let labelExtracted = false;

          // If getSummaryLabelFromValue is true and value is an object, extract label directly
          if (
            field.props?.['getSummaryLabelFromValue'] === true &&
            typeof value === 'object' &&
            !Array.isArray(value)
          ) {
            const valueObj = value as Record<string, unknown>;
            if (valueObj[bindLabel]) {
              value = valueObj[bindLabel];
              labelExtracted = true;
            }
          }

          // Fallback to options lookup if label was not extracted
          if (
            !labelExtracted &&
            (typeof value === 'string' || typeof value === 'number')
          ) {
            const options = field.props?.['options'] as Record<
              string,
              unknown
            >[];
            const bindValue = field.props?.['bindValue'] || 'value';

            if (options && Array.isArray(options)) {
              const matchedOption = options.find(
                opt => opt[bindValue] === value
              );
              if (matchedOption && matchedOption[bindLabel]) {
                value = matchedOption[bindLabel];
              }
            }
          }
        }
        if (
          typeof value === 'string' &&
          value.length > 0 &&
          typeof value !== 'number'
        ) {
          const stringValue: any = value as string;

          if (translateService) {
            const translatedValue = translateService.instant(stringValue);
            value =
              translatedValue !== stringValue
                ? translatedValue
                : getServiceTranslation(stringValue, serviceTranslations) ??
                  value;
          } else {
            value =
              getServiceTranslation(stringValue, serviceTranslations) ?? value;
          }
        }

        if (Array.isArray(value) && typeof value?.[0] !== 'object') {
          const stringValue: any = value.map((valueItem: any) => {
            if (translateService) {
              const translatedValue = translateService.instant(
                String(valueItem)
              );
              valueItem =
                translatedValue !== valueItem
                  ? translatedValue
                  : getServiceTranslation(valueItem, serviceTranslations) ??
                    valueItem;
              return valueItem;
            } else {
              return (
                getServiceTranslation(valueItem, serviceTranslations) ??
                valueItem
              );
            }
          });
          value = stringValue;
        }

        fieldGroup.push({
          fieldType: field.type as string,
          fieldBindLabel: getBindLabel(field),
          fieldName: field.key as string,
          fieldLabel: field?.props?.label as string,
          fieldValue: value,
          order,
        });
        break;
      }
      case 'customserviceviewer':
        // For customserviceviewer fields, we'll handle this case separately
        break;
      case 'customsingleservicerender':
        // For customsingleservicerender fields, we'll handle this case separately
        // No need to add anything here as it's processed at a higher level
        break;
      default:
        if (!value || (value instanceof String && value?.length <= 0)) {
          break;
        }
        if (field.props?.['currencyFormatting']) {
          if (typeof value === 'number') {
            const formattedValue = formatNumberWithCommas(value);
            value = formattedValue;
          }
        }
        fieldGroup.push({
          fieldType: field.type as string,
          fieldBindLabel: getBindLabel(field),
          fieldName: field.key as string,
          fieldLabel: field?.props?.label as string,
          fieldValue: value,
          order,
        });
        break;
    }
  });
  return { fieldGroup, fieldArray };
};

const getBindLabel = (field: FormlyFieldConfig): string | undefined => {
  if (
    !(
      field.type === 'customdropdown' ||
      field.type === 'customdropdownpaginated' ||
      field.type === 'customdropdowndatafetchpaginated'
    )
  )
    return '';
  return field?.props?.['bindLabel'];
};

const setCountOnLabel = (setCount = false, index = 0) => {
  return setCount ? `#${index + 1}` : '';
};

const getNestedFields = (
  repeaterField: FormlyFieldConfig,
  addLabelCount: boolean,
  index: number,
  section: FormlyFieldConfig,
  layoutData: Record<string, unknown>,
  serviceTranslations?: IServiceTranslation,
  order?: number,
  translateService?: TranslateService
): IFieldGroupNameData | null => {
  let nestedBlockData: IFieldData[] = [];
  let nestedFieldArray: IFieldArrayData[] | undefined = undefined;

  if (repeaterField.fieldGroup) {
    const result = getFieldData(
      section,
      repeaterField,
      layoutData,
      serviceTranslations,
      addLabelCount,
      true,
      translateService
    );
    nestedBlockData = result.fieldGroup;
    nestedFieldArray = result.fieldArray; // Also capture fieldArray!
  }

  const fieldGroupData: IFieldGroupNameData = {
    fieldGroupName: (repeaterField?.props?.label +
      setCountOnLabel(addLabelCount, index)) as string,
    fieldGroupKey: repeaterField.key as string,
    fieldGroup: nestedBlockData.length > 0 ? nestedBlockData : undefined,
    fieldArray: nestedFieldArray, // Include fieldArray!
    order,
  };

  return fieldGroupData.fieldGroup || fieldGroupData.fieldArray
    ? fieldGroupData
    : null;
};

const createLocationFields = (
  configs: Record<string, unknown>[],
  value: unknown,
  field: FormlyFieldConfig,
  fieldParentKey: string,
  order?: number
): IFieldData[] => {
  return configs
    .map(ele => {
      const _value = (<Record<string, unknown>>value)[field.key as string];
      return {
        fieldType: 'customdropdown',
        fieldBindLabel: ele['bindlabel'] as string,
        fieldName: ele['key'] as string,
        fieldLabel: ele['label'] as string,
        fieldParentKey: fieldParentKey,
        fieldValue: _value
          ? (_value as Record<string, unknown>)[ele['key'] as string]
          : '',
        order,
      };
    })
    .filter(x => x.fieldValue);
};

const getServiceTranslation = (
  key: any,
  serviceTranslations?: IServiceTranslation
): string | undefined => {
  const locale = localStorage.getItem('locale') ?? 'en-US';
  return serviceTranslations?.[locale]?.[key];
};

function formatNumberWithCommas(value: any): string {
  return Number(value).toLocaleString('en-US');
}

/**
 * Get nested value from an object using dot notation
 * @param obj The object to traverse
 * @param path The dot-separated path (e.g., 'user.name')
 * @returns The value at the path or undefined
 */
function getNestedValue(obj: any, path: string): any {
  if (!obj || !path) return undefined;

  return path.split('.').reduce((current, key) => {
    return current?.[key];
  }, obj);
}

/**
 * Interpolate template variables in a string
 * @param template The template string with {{variable}} placeholders
 * @param context The context object with variable values
 * @returns The interpolated string
 */
function interpolateLabel(
  template: string,
  context: Record<string, any>
): string {
  if (!template || typeof template !== 'string') return template;

  return template.replace(/\{\{(\w+)\}\}/g, (match, fieldName) => {
    return context[fieldName] || match;
  });
}

/**
 * Extended version of getNestedFields that supports label context for dynamic repeaters
 */
const getNestedFieldsWithContext = (
  repeaterField: FormlyFieldConfig,
  addLabelCount: boolean,
  index: number,
  section: FormlyFieldConfig,
  layoutData: Record<string, unknown>,
  serviceTranslations?: IServiceTranslation,
  order?: number,
  translateService?: TranslateService,
  labelContext?: Record<string, any>
): IFieldGroupNameData | null => {
  let nestedBlockData: IFieldData[] = [];
  let nestedFieldArray: IFieldArrayData[] | undefined = undefined;

  if (repeaterField.fieldGroup) {
    // Process nested fields and apply label interpolation
    const rawFieldData = getFieldData(
      section,
      repeaterField,
      layoutData,
      serviceTranslations,
      addLabelCount,
      true,
      translateService
    );

    // Apply label interpolation to all field labels if context is provided
    if (labelContext) {
      nestedBlockData = rawFieldData.fieldGroup.map(field => ({
        ...field,
        fieldLabel: interpolateLabel(field.fieldLabel, labelContext),
      }));
    } else {
      nestedBlockData = rawFieldData.fieldGroup;
    }

    nestedFieldArray = rawFieldData.fieldArray; // Also capture fieldArray!
  }

  const fieldGroupName = labelContext
    ? interpolateLabel(repeaterField?.props?.label as string, labelContext)
    : ((repeaterField?.props?.label +
        setCountOnLabel(addLabelCount, index)) as string);

  const fieldGroupData: IFieldGroupNameData = {
    fieldGroupName,
    fieldGroupKey: repeaterField.key as string,
    fieldGroup: nestedBlockData.length > 0 ? nestedBlockData : undefined,
    fieldArray: nestedFieldArray, // Include fieldArray!
    order,
  };

  return fieldGroupData.fieldGroup || fieldGroupData.fieldArray
    ? fieldGroupData
    : null;
};

/**
 * Process a nested repeater (customrepeater or customdynamicrepeater) inside a parent repeater
 * @param repeaterField The repeater field configuration
 * @param parentRepeaterItemValue The value of the parent repeater item containing this nested repeater
 * @param section The section configuration
 * @param serviceTranslations Service translations
 * @param addLabelCount Whether to add label count
 * @param order The order index
 * @param translateService The translate service
 * @param isCustomDynamicRepeater Whether this is a customdynamicrepeater (true) or customrepeater (false)
 * @returns IFieldGroupNameData wrapping the nested repeater, or null if no data
 */
const processNestedRepeater = (
  repeaterField: FormlyFieldConfig,
  parentRepeaterItemValue: Record<string, unknown>,
  section: FormlyFieldConfig,
  serviceTranslations?: IServiceTranslation,
  addLabelCount = false,
  order?: number,
  translateService?: TranslateService,
  isCustomDynamicRepeater = false
): IFieldGroupNameData | null => {
  // Extract the nested repeater value from the parent repeater item
  const nestedRepeaterValue =
    parentRepeaterItemValue[repeaterField.key as string];

  if (!nestedRepeaterValue) {
    return null;
  }

  // Create the nested repeater data structure
  const nestedFieldArrayData: IFieldArrayData = {
    fieldArrayKey: repeaterField.key as string,
    arrayItems: [],
    order,
  };

  const nestedFieldArrayGroup: FormlyFieldConfig[] =
    (<FormlyFieldConfig>repeaterField.fieldArray)?.fieldGroup ?? [];

  if (nestedFieldArrayGroup.length === 0) {
    return null;
  }

  // For customdynamicrepeater, value is already an array
  // For customrepeater, we need to convert object keys to array
  const isValueArray = Array.isArray(nestedRepeaterValue);
  const isValueObject =
    typeof nestedRepeaterValue === 'object' && !isValueArray;

  if (!isValueArray && !isValueObject) {
    return null;
  }

  // Get the dynamic repeater configuration if applicable
  const dynamicConfig = repeaterField.props?.['dynamicRepeaterConfig'] || {};
  const itemIdField = dynamicConfig.itemIdField || 'id';
  const itemLabelField = dynamicConfig.itemLabelField || 'label';
  const itemsData = repeaterField.props?.['items'] || [];

  // Convert to array format for processing
  const nestedItemsArray: any[] =
    isCustomDynamicRepeater && isValueArray
      ? (nestedRepeaterValue as any[])
      : Object.keys(nestedRepeaterValue as object).map(
          (_, idx) => (<Record<string, unknown>>nestedRepeaterValue)[idx]
        );

  // Process each item in the nested repeater
  nestedFieldArrayData.arrayItems = nestedItemsArray
    .map((nestedItem: any, nestedIndex: number) => {
      if (!nestedItem) {
        return null;
      }

      const nestedFieldGroup: IFieldData[] = [];
      const nestedFormGroup: IFieldGroupNameData[] = [];

      // Get the corresponding item data for dynamic labels (for customdynamicrepeater)
      const itemData = itemsData[nestedIndex] || {};
      const itemLabel =
        getNestedValue(itemData, itemLabelField) || `Item ${nestedIndex + 1}`;
      const itemId = getNestedValue(itemData, itemIdField);

      // Create a context for label interpolation
      const labelContext = isCustomDynamicRepeater
        ? {
            itemLabel,
            itemId,
            label: itemLabel,
            id: itemId,
            ...itemData,
          }
        : undefined;

      // Process each field in the nested repeater
      nestedFieldArrayGroup.forEach((nestedField: FormlyFieldConfig) => {
        // Skip hidden fields for customdynamicrepeater
        if (
          isCustomDynamicRepeater &&
          (nestedField.props?.['hideField'] ||
            nestedField.key === 'itemId' ||
            nestedField.key === '_isApiPopulated')
        ) {
          return;
        }

        switch (nestedField.type) {
          case 'block': {
            // For nested blocks inside the nested repeater
            const formGroupData =
              isCustomDynamicRepeater && labelContext
                ? getNestedFieldsWithContext(
                    nestedField,
                    addLabelCount,
                    nestedIndex,
                    section,
                    nestedItem as Record<string, unknown>,
                    serviceTranslations,
                    nestedIndex,
                    translateService,
                    labelContext
                  )
                : getNestedFields(
                    nestedField,
                    addLabelCount,
                    nestedIndex,
                    section,
                    nestedItem as Record<string, unknown>,
                    serviceTranslations,
                    nestedIndex,
                    translateService
                  );

            if (formGroupData) {
              if (isCustomDynamicRepeater && labelContext) {
                formGroupData.fieldGroupName = interpolateLabel(
                  formGroupData.fieldGroupName,
                  labelContext
                );
              }
              nestedFormGroup.push(formGroupData);
            }
            break;
          }
          case 'customcascadingdropdowns': {
            nestedFieldGroup.push(
              ...createLocationFields(
                nestedField?.props?.['configs'],
                nestedItem,
                nestedField,
                nestedField['key'] as string,
                nestedIndex
              )
            );
            break;
          }
          case 'customdatatable': {
            buildCustomDatatablePreviewData(
              (<Record<string, unknown>>nestedItem)[nestedField.key as string],
              nestedFieldGroup,
              nestedField,
              nestedIndex
            );
            break;
          }
          case 'customrepeater':
          case 'customdynamicrepeater': {
            // Recursive case: Handle triple-nested repeaters
            const tripleNestedRepeaterData = processNestedRepeater(
              nestedField,
              nestedItem as Record<string, unknown>,
              section,
              serviceTranslations,
              addLabelCount,
              nestedIndex,
              translateService,
              nestedField.type === 'customdynamicrepeater'
            );
            if (tripleNestedRepeaterData) {
              if (isCustomDynamicRepeater && labelContext) {
                tripleNestedRepeaterData.fieldGroupName = interpolateLabel(
                  tripleNestedRepeaterData.fieldGroupName,
                  labelContext
                );
              }
              nestedFormGroup.push(tripleNestedRepeaterData);
            }
            break;
          }
          default: {
            if (
              (<Record<string, unknown>>nestedItem)[nestedField.key as string]
            ) {
              const fieldLabel =
                isCustomDynamicRepeater && labelContext
                  ? interpolateLabel(
                      nestedField?.props?.label as string,
                      labelContext
                    )
                  : ((nestedField?.props?.label +
                      setCountOnLabel(addLabelCount, nestedIndex)) as string);

              nestedFieldGroup.push({
                fieldType: nestedField.type as string,
                fieldBindLabel:
                  (nestedField?.props?.['bindLabel'] as string) ?? '',
                fieldName: nestedField.key as string,
                fieldLabel: fieldLabel,
                fieldValue: (<Record<string, unknown>>nestedItem)[
                  nestedField.key as string
                ],
                order: nestedIndex,
              });
            }
            break;
          }
        }
      });

      return {
        fieldGroup: nestedFieldGroup,
        formGroup: nestedFormGroup,
      };
    })
    .filter(item => item !== null) as IFieldArrayDataFields[];

  // If no items were processed, return null
  if (nestedFieldArrayData.arrayItems.length === 0) {
    return null;
  }

  // Wrap the nested repeater in a field group and return
  return {
    fieldGroupName: repeaterField?.props?.label as string,
    fieldGroupKey: repeaterField.key as string,
    fieldGroup: undefined,
    fieldArray: [nestedFieldArrayData],
    order,
  };
};

/**
 * Process a customsingleservicerender field to generate preview data
 */
export const processSingleServiceRender = (
  field: FormlyFieldConfig,
  layoutData: Record<string, unknown>,
  serviceTranslations?: IServiceTranslation,
  addLabelCount = false,
  translateService?: TranslateService,
  buildPreviewDataFn?: any
): IApplicationFormData[] => {
  const serviceKey = field.key as string;
  const serviceLabel = field.props?.label ?? '';
  const serviceCode = field.props?.['code'] ?? '';

  // Get the service data from the model
  const serviceData = layoutData[serviceCode] as Record<string, unknown>;

  if (!serviceData) {
    return [];
  }

  // Process the service form layout to generate preview data
  let servicePreviewData: IApplicationFormData[] = [];

  // If the service has a fieldGroup with a form structure, process it
  if (field.fieldGroup && field.fieldGroup.length > 0 && buildPreviewDataFn) {
    // Use the standard processing for the service's internal fields
    servicePreviewData = buildPreviewDataFn(
      field.fieldGroup,
      serviceData,
      serviceTranslations,
      addLabelCount,
      translateService
    );
  } else if (serviceData['__formLayout'] && buildPreviewDataFn) {
    const formLayout = serviceData['__formLayout'] as FormlyFieldConfig[];

    const cleanedServiceData = cleanServiceData(serviceData);

    // Process with stored layout
    servicePreviewData = buildPreviewDataFn(
      formLayout,
      cleanedServiceData,
      serviceTranslations,
      addLabelCount,
      translateService
    );
  }

  // Create a properly structured service section that preserves internal section structure
  if (servicePreviewData.length > 0) {
    // Create a service section that contains the internal sections as nested sections
    // This preserves the internal section structure within the service
    const nestedServiceSections: IFieldGroupNameData[] = servicePreviewData.map(
      (section, index) => ({
        fieldGroupName: section.formGroupName,
        fieldGroupKey: section.formGroupKey,
        fieldGroup: section.formGroup[0]?.fieldGroup || [], // Extract the actual field data
        fieldArray: section.formGroup[0]?.fieldArray,
        // Add a marker to indicate this is a service section for styling purposes
        isServiceSection: true,
        sectionNumber: index + 1,
      })
    );

    // Return a service section containing all internal sections in the nestedSections property
    return [
      {
        formGroupName: serviceLabel,
        formGroupKey: serviceKey,
        formGroup: [], // Empty regular form group
        nestedSections: nestedServiceSections, // Service sections go here
        isService: true, // Mark this as a service for special handling in UI
      },
    ];
  }

  return [];
};
